import { Paper, Typography, Box, TextField, Button, Alert } from '@mui/material';
import { captureException } from '@sentry/react';
import React, { FC, useState, useEffect } from 'react';
import {
  useIntegrationsList,
  useUpdatePersonalkollenIntegrationMutation,
} from '../../../stores/FetcherStores/integrationFetcher';
import useCompanyId from '../../../hooks/useCompanyId';
import { IntegrationPersonalkollen } from '../../../model/Integration';

interface PersonalkollenIntegrationProps {
  integrationId: string;
}

const PersonalkollenIntegration: FC<PersonalkollenIntegrationProps> = ({ integrationId }) => {
  const companyId = useCompanyId();
  const { data: integrations } = useIntegrationsList(companyId);
  const { mutate: updateIntegration, isLoading } = useUpdatePersonalkollenIntegrationMutation();
  const [apiKey, setApiKey] = useState('');
  const [hasChanges, setHasChanges] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const personalkollenIntegration = integrations?.find(
    (integration) => integration.type === 'PERSONALKOLLEN' && integration.id === integrationId,
  ) as IntegrationPersonalkollen | undefined;

  useEffect(() => {
    if (personalkollenIntegration?.apiKey) {
      setApiKey(personalkollenIntegration.apiKey);
      setHasChanges(false);
    }
  }, [personalkollenIntegration]);

  if (!personalkollenIntegration) {
    return (
      <Paper sx={{ padding: 2 }}>
        <Typography variant="h2" gutterBottom>
          Integreringen hittades inte
        </Typography>
      </Paper>
    );
  }

  const handleApiKeyChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newApiKey = event.target.value;
    setApiKey(newApiKey);
    setHasChanges(newApiKey !== personalkollenIntegration.apiKey);
  };

  const handleSave = async () => {
    try {
      await updateIntegration(companyId, integrationId, {
        apiKey,
      });
      setHasChanges(false);
      setError(null);
    } catch (e) {
      captureException(e);
      setError('Något gick fel, försök igen senare, eller kontakta support.');
    }
  };

  return (
    <Box>
      <Typography variant="h2" gutterBottom>
        Personalkollen
      </Typography>
      <Box mt={2}>
        {error && (
          <Box mb={2}>
            <Alert severity="error">{error}</Alert>
          </Box>
        )}
        <TextField
          fullWidth
          label="API nyckel"
          value={apiKey}
          onChange={handleApiKeyChange}
          type="password"
          variant="outlined"
        />
        <Box mt={2}>
          <Button variant="contained" onClick={handleSave} disabled={!hasChanges || isLoading}>
            Spara
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default PersonalkollenIntegration;
